import { useState, useEffect } from 'react';
import { Video, Plus, Edit, Trash2, X } from 'lucide-react';

export default function VideosAdmin() {
  const [videos, setVideos] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [videoSeleccionado, setVideoSeleccionado] = useState(null);

  useEffect(() => { cargarVideos(); }, []);

  const cargarVideos = async () => {
    try {
      const res = await fetch('https://logia-production-725b.up.railway.app/api/videos');
      const data = await res.json();
      if (data.success) setVideos(data.data);
    } catch (error) { console.error("Error:", error); }
  };

  const abrirModal = (video = null) => {
    setVideoSeleccionado(video);
    setIsModalOpen(true);
  };

  // Crear (POST) y Actualizar (PUT)
  const handleGuardar = async (formData) => {
    const url = videoSeleccionado 
      ? `https://logia-production-725b.up.railway.app/api/admin/videos/${videoSeleccionado.id}`
      : 'https://logia-production-725b.up.railway.app/api/admin/videos';
    const method = videoSeleccionado ? 'PUT' : 'POST';

    try {
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (data.success) {
        setIsModalOpen(false);
        cargarVideos();
      } else alert('Error al guardar: ' + data.error);
    } catch (error) { alert('Error de conexión.'); }
  };

  // Eliminar (DELETE)
  const handleEliminar = async (id) => {
    if (!window.confirm("¿Estás seguro de eliminar este video?")) return;
    try {
      const res = await fetch(`https://logia-production-725b.up.railway.app/api/admin/videos/${id}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (data.success) {
        cargarVideos();
      } else alert('Error al eliminar: ' + data.error);
    } catch (error) { alert('Error de conexión.'); }
  };

  return (
    <div className="flex flex-col gap-6 animate-fade-in relative">
      <div className="flex justify-between items-center bg-[#0a0a0a] border border-white/10 p-6 shadow-lg relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-10 mix-blend-overlay pointer-events-none"></div>
        <div className="relative z-10">
          <h1 className="font-serif text-3xl md:text-4xl text-[#C5A059] m-0 tracking-wide flex items-center gap-3">
            <Video size={32} /> Gestión de Videos
          </h1>
          <p className="text-gray-400 font-light text-sm mt-2 m-0">Administra la videoteca pública e institucional.</p>
        </div>
        <button onClick={() => abrirModal()} className="relative z-10 flex items-center gap-2 px-6 py-3 text-xs tracking-widest uppercase font-bold bg-[#C5A059] text-[#050505] hover:bg-white transition-colors">
          <Plus size={16} /> Subir Video
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {videos.map((video) => (
          <div key={video.id} className="bg-[#050505] border border-white/5 p-6 shadow-lg hover:border-[#C5A059]/30 transition-colors flex flex-col justify-between">
            <div>
              <div className="flex justify-between items-start mb-4">
                <span className={`px-2 py-1 text-[9px] uppercase tracking-widest font-mono border ${video.estado === 'publicado' ? 'bg-green-900/20 text-green-400 border-green-800/50' : 'bg-yellow-900/20 text-yellow-400 border-yellow-800/50'}`}>
                  {video.estado}
                </span>
              </div>
              <h4 className="text-white text-lg font-medium tracking-wide m-0 mb-2">{video.titulo}</h4>
              <a href={video.url} target="_blank" rel="noreferrer" className="text-[#C5A059] text-xs hover:underline break-all block mb-4">{video.url}</a>
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
              <button onClick={() => abrirModal(video)} className="text-gray-500 hover:text-[#C5A059] transition-colors"><Edit size={16} /></button>
              <button onClick={() => handleEliminar(video.id)} className="text-gray-500 hover:text-red-500 transition-colors"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>

      {isModalOpen && <VideoModal videoData={videoSeleccionado} onClose={() => setIsModalOpen(false)} onSave={handleGuardar} />}
    </div>
  );
}

// En el componente VideoModal de VideosAdmin.jsx
function VideoModal({ videoData, onClose, onSave }) {
  // 1. CAMBIAR 'url' por 'youtube_url'
  const [formData, setFormData] = useState({
    titulo: videoData?.titulo || '',
    youtube_url: videoData?.youtube_url || ''
  });

  const handleSubmit = (e) => { e.preventDefault(); onSave(formData); };

  return (
    <div className="fixed inset-0 z-[999] flex justify-center items-start pb-10 px-4 sm:px-6 bg-[#050505]/90 backdrop-blur-sm">
      <div className="bg-[#0a0a0a] border border-[#C5A059]/30 w-full max-w-lg shadow-2xl relative flex flex-col max-h-[calc(100vh-160px)] animate-fade-in">
        <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#C5A059] to-transparent opacity-50 shrink-0"></div>
        
        <div className="flex justify-between items-center p-5 md:p-6 border-b border-white/10 shrink-0">
          <h2 className="font-serif text-xl md:text-2xl text-white m-0">{videoData ? 'Editar Video' : 'Nuevo Video'}</h2>
          <button onClick={onClose} className="bg-transparent border-none text-gray-500 hover:text-white cursor-pointer p-1"><X size={20} /></button>
        </div>
        
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="p-5 md:p-6 overflow-y-auto flex-1 flex flex-col gap-4 hide-scrollbar">
            <div className="flex flex-col gap-2">
              <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">Título del Video</label>
              <input type="text" value={formData.titulo} onChange={e => setFormData({...formData, titulo: e.target.value})} required className="bg-[#050505] border border-white/10 text-white px-3 py-3 text-sm focus:outline-none focus:border-[#C5A059]" />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase font-semibold">URL (YouTube / Vimeo)</label>
              {/* 2. CAMBIAR 'formData.url' por 'formData.youtube_url' aquí también */}
              <input type="url" value={formData.youtube_url} onChange={e => setFormData({...formData, youtube_url: e.target.value})} required className="bg-[#050505] border border-white/10 text-white px-3 py-3 text-sm focus:outline-none focus:border-[#C5A059]" />
            </div>
          </div>

          <div className="p-5 border-t border-white/10 flex justify-end gap-3 shrink-0 bg-[#050505]">
            <button type="button" onClick={onClose} className="bg-transparent border border-white/20 text-gray-300 px-6 py-3 text-xs tracking-widest uppercase hover:bg-white/5 transition-colors">Cancelar</button>
            <button type="submit" className="bg-[#C5A059] hover:bg-white text-[#050505] px-6 py-3 text-xs tracking-widest uppercase font-bold transition-colors border-none">Guardar</button>
          </div>
        </form>
      </div>
    </div>
  );
}